import React, { useRef, useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import "./ProfilePictureModal.css";

/**
 * ProfilePictureModal
 *
 * Two use cases:
 *   1. User profile picture (brandId = null) → /users/me/profile-picture
 *   2. Brand avatar (brandId set) → /users/me/brands/:id/profile-picture
 *
 * The brand-photo case is critical for the AI face-detection pipeline —
 * the worker uses this image to recognize the creator in source video
 * and keep them centered in clips. Copy is explicit about this.
 *
 * Built-in zoom (1–3x) + pan crop. Outputs a square 512×512 JPEG so the
 * face landmark detector has consistent input.
 *
 * Props:
 *   isOpen, onClose, brandId, brandName, onUploaded
 */

const OUTPUT_SIZE = 512;       // saved square JPEG dimension
const PREVIEW_SIZE = 220;      // on-screen circle diameter
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

const ProfilePictureModal = ({ isOpen, onClose, brandId = null, brandName = null, onUploaded }) => {
  const fileInputRef = useRef(null);
  const previewBoxRef = useRef(null);
  const imgRef = useRef(null);

  const [file, setFile] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);     // object URL of selected file
  const [imgDims, setImgDims] = useState(null);   // { w, h } of natural image
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // px offset from centered
  const [uploading, setUploading] = useState(false);

  // Drag state held in a ref so it doesn't trigger re-renders mid-gesture
  const dragRef = useRef({ active: false, lastX: 0, lastY: 0, startPanX: 0, startPanY: 0 });

  const reset = useCallback(() => {
    setFile(null);
    setImgSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setImgDims(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Revoke object URL when component closes
  useEffect(() => {
    if (!isOpen) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const isBrand = !!brandId;

  const title = isBrand
    ? `Photo for ${brandName || 'this streamer'}`
    : 'Add a profile picture';

  // Brand subtitle is intentionally explicit: this image trains the
  // face-detection step in the clip pipeline.
  const subtitle = isBrand
    ? `Upload a clear, well-lit photo of the person who's getting clipped (yourself, the streamer, or the talent). Our AI uses this to detect their face in the video and keep them in frame.`
    : "Upload a photo. You'll be able to zoom and drag to pick the perfect crop.";

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (imgSrc) URL.revokeObjectURL(imgSrc);
    const url = URL.createObjectURL(f);
    setFile(f);
    setImgSrc(url);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // When the image finishes loading, capture its natural dimensions so
  // we can compute the cover-fit scale + canvas crop math.
  const handleImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    setImgDims({ w: el.naturalWidth, h: el.naturalHeight });
  };

  // Cover-fit base scale: smallest scale that fully covers the preview.
  const baseFit = imgDims
    ? Math.max(PREVIEW_SIZE / imgDims.w, PREVIEW_SIZE / imgDims.h)
    : 1;
  const effectiveScale = baseFit * zoom;

  const renderedW = imgDims ? imgDims.w * effectiveScale : PREVIEW_SIZE;
  const renderedH = imgDims ? imgDims.h * effectiveScale : PREVIEW_SIZE;

  // Clamp pan so the image always covers the circle
  const clampPan = (p) => {
    const maxX = Math.max(0, (renderedW - PREVIEW_SIZE) / 2);
    const maxY = Math.max(0, (renderedH - PREVIEW_SIZE) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, p.x)),
      y: Math.max(-maxY, Math.min(maxY, p.y)),
    };
  };

  const imgLeft = (PREVIEW_SIZE - renderedW) / 2 + pan.x;
  const imgTop  = (PREVIEW_SIZE - renderedH) / 2 + pan.y;

  // ── Drag handlers ─────────────────────────────────────────────────
  const onPointerDown = (e) => {
    if (!imgDims) return;
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      active: true,
      lastX: e.clientX,
      lastY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    setPan(clampPan({
      x: dragRef.current.startPanX + dx,
      y: dragRef.current.startPanY + dy,
    }));
  };
  const onPointerUp = (e) => {
    dragRef.current.active = false;
    try { e.target.releasePointerCapture?.(e.pointerId); } catch (_) {}
  };

  // ── Zoom (wheel + slider) ─────────────────────────────────────────
  const onWheel = (e) => {
    if (!imgDims) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom + delta));
    setZoom(next);
    // Re-clamp after zoom change since bounds shifted
    setPan((p) => clampPan(p));
  };

  // ── Save: crop to OUTPUT_SIZE square JPEG, upload ────────────────
  const handleUpload = async () => {
    if (!file && !imgRef.current) {
      fileInputRef.current?.click();
      return;
    }
    if (!imgRef.current || !imgDims) return;

    setUploading(true);
    try {
      // Derive source rectangle in IMAGE coords from the current preview
      // transform. Same math as the on-screen positioning.
      const sx = -imgLeft / effectiveScale;
      const sy = -imgTop  / effectiveScale;
      const sw = PREVIEW_SIZE / effectiveScale;
      const sh = PREVIEW_SIZE / effectiveScale;

      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(imgRef.current, sx, sy, sw, sh, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92)
      );
      if (!blob) throw new Error("Could not crop image");

      // The backend's multer field name differs between endpoints
      const formData = new FormData();
      let newUrl;
      if (isBrand) {
        formData.append("avatar", blob, "avatar.jpg");
        const res = await axiosInstance.post(
          `/users/me/brands/${brandId}/profile-picture`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        newUrl = res.data.avatar;
      } else {
        formData.append("profilePicture", blob, "profile.jpg");
        const res = await axiosInstance.post(
          "/users/me/profile-picture",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        newUrl = res.data.profilePicture;
        window.dispatchEvent(new CustomEvent("profile_picture_updated", {
          detail: { profilePicture: newUrl },
        }));
      }
      toast.success(isBrand ? `${brandName || 'Streamer'} photo updated` : "Profile picture updated");
      onUploaded?.(newUrl, brandId);
      reset();
      onClose();
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Failed to upload. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div className="ppm-overlay" onClick={handleClose}>
      <div className="ppm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ppm-header">
          <span className="ppm-logo">CLIPBAIT</span>
          <button className="ppm-close" onClick={handleClose} aria-label="Close">✕</button>
        </div>

        <div className="ppm-body">
          <h2 className="ppm-title">{title}</h2>
          <p className="ppm-subtitle">{subtitle}</p>

          {/* Tip card — extra-loud for brand uploads since the AI depends
              on a real face being recognizable in this image. */}
          {isBrand && (
            <div className="ppm-tip">
              <span className="ppm-tip-icon">!</span>
              <span>
                <b>Face on screen, not a logo.</b> Upload a real photo of the
                person — a logo or icon will stop the AI from tracking them.
              </span>
            </div>
          )}

          {/* Preview / crop area */}
          <div
            className="ppm-crop-wrap"
            style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
          >
            <div
              ref={previewBoxRef}
              className="ppm-crop-box"
              style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
              onWheel={imgSrc ? onWheel : undefined}
              onPointerDown={imgSrc ? onPointerDown : undefined}
              onPointerMove={imgSrc ? onPointerMove : undefined}
              onPointerUp={imgSrc ? onPointerUp : undefined}
              onPointerCancel={imgSrc ? onPointerUp : undefined}
              onClick={!imgSrc ? () => fileInputRef.current?.click() : undefined}
            >
              {imgSrc ? (
                <img
                  ref={imgRef}
                  src={imgSrc}
                  alt=""
                  draggable={false}
                  onLoad={handleImgLoad}
                  style={{
                    position: "absolute",
                    left: `${imgLeft}px`,
                    top: `${imgTop}px`,
                    width: `${renderedW}px`,
                    height: `${renderedH}px`,
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              ) : (
                <div className="ppm-avatar-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ff6a00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  <span>Click to choose photo</span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>

          {imgSrc && (
            <>
              <div className="ppm-crop-hint">
                Drag to reposition · scroll or pinch to zoom
              </div>
              <div className="ppm-zoom-row">
                <span className="ppm-zoom-label">Zoom</span>
                <input
                  type="range"
                  min={ZOOM_MIN}
                  max={ZOOM_MAX}
                  step="0.01"
                  value={zoom}
                  onChange={(e) => {
                    setZoom(parseFloat(e.target.value));
                    setPan((p) => clampPan(p));
                  }}
                  className="ppm-zoom-slider"
                />
                <span className="ppm-zoom-value">{zoom.toFixed(1)}x</span>
              </div>
              <button
                type="button"
                className="ppm-change-link"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose a different photo
              </button>
            </>
          )}

          <button
            type="button"
            className="ppm-upload-btn"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? <span className="ppm-spinner" /> : imgSrc ? "Save photo" : "Choose photo"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureModal;
